import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, KpiCard } from "@/components/dashboard/widgets";
import { Loader2, TrendingUp, MousePointerClick, UserPlus, Percent, PoundSterling, Sparkles, Layers } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";

/**
 * Affiliate Analytics (Pack E)
 * Honest funnel breakdown — daily clicks/signups + per-source rows
 * (utm_source × campaign) ranked by clicks, with conversion + EPC.
 */
export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/affiliate/analytics?days=${days}`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="space-y-6" data-testid="affiliate-analytics">
      <SectionTitle
        eyebrow="Performance"
        title="Analytics & sources"
        subtitle="Where your traffic comes from, where it converts, and what each click is worth."
      />

      <div className="flex items-center gap-2">
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 text-[12px] rounded-full ${days === d ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid={`aff-an-period-${d}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20"><Loader2 className="size-5 text-slate-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard label="Clicks"     value={data.totals.clicks.toLocaleString()}    icon={MousePointerClick} accent="blue"    testid="aff-an-clicks" />
            <KpiCard label="Signups"    value={data.totals.signups.toLocaleString()}   icon={UserPlus}          accent="blue"    testid="aff-an-signups" />
            <KpiCard label="Conv. rate" value={`${data.totals.conv_pct}%`}             icon={Percent}           accent="emerald" testid="aff-an-conv" />
            <KpiCard label="Commission" value={data.totals.commission_gbp.toLocaleString()} prefix="£" icon={PoundSterling} accent="emerald" testid="aff-an-commission" />
            <KpiCard label="EPC"        value={data.totals.epc_gbp.toFixed(2)} prefix="£" icon={TrendingUp}      accent="emerald" testid="aff-an-epc" />
          </div>

          <Card className="rounded-xl border-slate-200 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles className="size-4 text-[#0055FF]" />
                <h3 className="font-medium text-slate-900">Daily traffic · last {days} days</h3>
              </div>
              <ResponsiveContainer width="100%" height={300} minHeight={200}>
                <AreaChart data={data.series} margin={{ left: -8, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="aff-ana-clicks" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#0055FF" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="aff-ana-signups" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Area type="monotone" dataKey="clicks"  stroke="#0055FF" strokeWidth={2.2} fill="url(#aff-ana-clicks)"  />
                  <Area type="monotone" dataKey="signups" stroke="#10B981" strokeWidth={2.2} fill="url(#aff-ana-signups)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-slate-200 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-5">
                <Layers className="size-4 text-[#0055FF]" />
                <h3 className="font-medium text-slate-900">By source</h3>
                <span className="text-[11.5px] text-slate-500 ml-2">Ranked by clicks · across all your tracking links</span>
              </div>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm" data-testid="aff-an-sources-table">
                  <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 text-left">
                    <tr>
                      <th className="py-2 px-2 font-medium">Source</th>
                      <th className="px-2 font-medium">Campaign</th>
                      <th className="px-2 font-medium text-right">Clicks</th>
                      <th className="px-2 font-medium text-right">Signups</th>
                      <th className="px-2 font-medium text-right">Conv %</th>
                      <th className="px-2 font-medium text-right">EPC</th>
                      <th className="px-2 font-medium text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.map((s, idx) => (
                      <tr key={`${s.utm_source}-${s.campaign}-${idx}`} className="border-t border-slate-100" data-testid={`aff-an-src-${idx}`}>
                        <td className="py-2.5 px-2 font-medium text-slate-900">{s.utm_source}</td>
                        <td className="px-2 text-slate-700">{s.campaign}</td>
                        <td className="px-2 text-right tabular-nums">{s.clicks.toLocaleString()}</td>
                        <td className="px-2 text-right tabular-nums">{s.signups.toLocaleString()}</td>
                        <td className="px-2 text-right tabular-nums">{s.conv_pct}%</td>
                        <td className="px-2 text-right tabular-nums">£{s.epc_gbp.toFixed(2)}</td>
                        <td className="px-2 text-right tabular-nums font-medium">£{s.commission_gbp.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                    {data.sources.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-500">No sources yet. Tag your links with UTM source + campaign to see them here.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
